
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const getFilePathFromUrl = (url: string): string | null => {
  if (!url) return null;
  
  // Extract file path from Supabase storage URL
  const urlParts = url.split('/');
  const storageIndex = urlParts.findIndex(part => part === 'storage');
  
  if (storageIndex !== -1 && urlParts[storageIndex + 2]) {
    // Return path after bucket name
    return urlParts.slice(storageIndex + 3).join('/');
  }
  
  return null;
};

export const deleteFileFromStorage = async (bucketName: string, filePath: string): Promise<boolean> => {
  try {
    const { error } = await supabase.storage
      .from(bucketName)
      .remove([filePath]);
    
    if (error) {
      console.error(`Error deleting file from ${bucketName}:`, error);
      return false;
    }
    
    console.log(`Successfully deleted file: ${filePath} from bucket: ${bucketName}`);
    return true;
  } catch (error) {
    console.error(`Failed to delete file from ${bucketName}:`, error);
    return false;
  }
};

export const deleteFileFromUrl = async (url: string, bucketName: string): Promise<boolean> => {
  if (!url) return true;
  
  const filePath = getFilePathFromUrl(url);
  if (!filePath) {
    // If we can't extract the path, it might be an external URL
    console.log('Could not extract file path from URL, assuming external URL');
    return true;
  }
  
  return await deleteFileFromStorage(bucketName, filePath);
};

export const sanitizeFileName = (fileName: string): string => {
  // Remove or replace unsafe characters for file storage
  return fileName
    .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace unsafe chars with underscore
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    .toLowerCase();
};

export const generateUniqueFileNameWithOriginal = (file: File): string => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 8);
  const fileExt = file.name.split('.').pop() || '';
  const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
  const sanitizedName = sanitizeFileName(nameWithoutExt);
  
  return `${timestamp}-${randomString}-${sanitizedName}.${fileExt}`;
};

export const validateImageFile = (file: File): { isValid: boolean; error?: string } => {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: "Please upload a JPEG, PNG, WebP, or GIF image."
    };
  }
  
  // 5MB limit for images
  if (file.size > 5 * 1024 * 1024) {
    return {
      isValid: false,
      error: "Please upload an image smaller than 5MB."
    };
  }
  
  return { isValid: true };
};

export const validateActivityFile = (file: File): { isValid: boolean; error?: string } => {
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/gif'
  ];

  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: "Please upload PDF, Word document, or image files only."
    };
  }

  // Validate file size (max 10MB)
  if (file.size > 10 * 1024 * 1024) {
    return {
      isValid: false,
      error: "Please upload files smaller than 10MB."
    };
  }

  return { isValid: true };
};

// Updated to use the centralized validation for activities
export const validateFileForBucket = (file: File, bucketName: string): { isValid: boolean; error?: string } => {
  switch (bucketName) {
    case 'activities':
      return validateActivityFile(file);
    case 'episode-thumbnails':
    case 'character-images':
    case 'partner-logos':
      return validateImageFile(file);
    default:
      return validateImageFile(file); // Default to image validation
  }
};

// Legacy function for backward compatibility
export const generateUniqueFileName = (file: File): string => {
  return generateUniqueFileNameWithOriginal(file);
};

export const extractOriginalFileName = (storagePath: string): string => {
  if (!storagePath) return "";
  
  const fileName = storagePath.split('/').pop() || "";
  
  // Check if it follows our new naming pattern: timestamp-random-originalname.ext
  const match = fileName.match(/^\d+-[a-z0-9]+-(.+)$/i);
  if (match) {
    // Extract the original name part and convert underscores back to spaces for display
    return match[1].replace(/_/g, ' ');
  }
  
  // Fallback: return the filename as-is if it doesn't match our pattern
  return fileName;
};
